import { Component, signal, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-email-verification',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    InputTextModule,
    ButtonModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './email-verification.component.html',
  styleUrl: './email-verification.component.css'
})
export class EmailVerificationComponent implements OnInit, OnDestroy {
  email = signal('');
  otp = signal('');
  loading = signal(false);
  sendingOtp = signal(false);
  expiresAt = signal<Date | null>(null);
  countdown = signal<number>(0);
  private countdownInterval: any;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    // Get email from query params or route state
    this.route.queryParams.subscribe(params => {
      if (params['email']) {
        this.email.set(params['email']);
        // OTP is already sent during registration, so we don't send it again here
        // User just needs to enter the OTP they received
        this.showSuccessToast('Please enter the verification code sent to your email');
      }
    });

    // Check if email is in route state (from registration)
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state?.['email']) {
      this.email.set(navigation.extras.state['email']);
      // OTP is already sent during registration, so we don't send it again here
      this.showSuccessToast('Please enter the verification code sent to your email');
    }
  }

  sendOtp(): void {
    const emailValue = this.email().trim();
    
    if (!emailValue) {
      this.showErrorToast('Please enter your email address');
      return;
    }

    if (!this.isValidEmail(emailValue)) {
      this.showErrorToast('Please enter a valid email address');
      return;
    }

    this.sendingOtp.set(true);
    this.otp.set('');

    this.authService.sendEmailVerificationOtp(emailValue).subscribe({
      next: (response) => {
        this.sendingOtp.set(false);
        if (response.success && response.data) {
          this.showSuccessToast('Verification code sent to your email!');
          if (response.data.expiresAt) {
            this.expiresAt.set(new Date(response.data.expiresAt));
            this.startCountdown();
          }
        } else {
          this.showErrorToast(response.message || 'Failed to send verification code');
        }
      },
      error: (err) => {
        this.sendingOtp.set(false);
        this.showErrorToast(err.error?.message || 'An error occurred. Please try again.');
      }
    });
  }

  verifyOtp(): void {
    const emailValue = this.email().trim();
    const otpValue = this.otp().trim();

    if (!otpValue) {
      this.showErrorToast('Please enter the verification code');
      return;
    }

    if (otpValue.length !== 6) {
      this.showErrorToast('Verification code must be 6 digits');
      return;
    }

    this.loading.set(true);

    this.authService.verifyEmailOtp(emailValue, otpValue).subscribe({
      next: (response) => {
        this.loading.set(false);
        if (response.success) {
          this.showSuccessToast('Email verified successfully! Redirecting to sign in...');
          // Redirect to sign in after 2 seconds
          setTimeout(() => {
            this.router.navigate(['/signin'], {
              queryParams: { verified: 'true', email: emailValue }
            });
          }, 2000);
        } else {
          this.showErrorToast(response.message || 'Invalid verification code');
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.showErrorToast(err.error?.message || 'Invalid verification code. Please try again.');
      }
    });
  }

  private showSuccessToast(message: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: message,
      life: 3000
    });
  }

  private showErrorToast(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 4000
    });
  }

  private startCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    const updateCountdown = () => {
      if (!this.expiresAt()) {
        return;
      }

      const now = new Date();
      const expires = this.expiresAt()!;
      const diff = Math.max(0, Math.floor((expires.getTime() - now.getTime()) / 1000));
      
      this.countdown.set(diff);

      if (diff <= 0) {
        clearInterval(this.countdownInterval);
        this.countdown.set(0);
      }
    };

    updateCountdown();
    this.countdownInterval = setInterval(updateCountdown, 1000);
  }

  formatCountdown(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }
}
